/* Peakside Facelift 2026 — VIN barcode scanner: a camera button beside every VIN box.
   Brent 2026-10-13: product pages (option-app VIN boxes, attached by pk-product-fields.js) and the tune builder
   (tune-configurator.js renderOrder) call PkVinScan.attach(input). The camera reads the VIN barcode on the
   driver's door jamb sticker (Code 39 / Code 128 / Data Matrix / QR), fills the box and fires an input event,
   so each page's own VIN check (and the builder's Year / Make / Model decode) runs as if the VIN was typed.
   Decoder: the browser's BarcodeDetector where it reads Code 39 (Chrome on Android / macOS); everywhere else the
   barcode-detector ponyfill (zxing-cpp WebAssembly) is loaded from jsDelivr on first use, pinned + SRI-checked.
   No camera / camera blocked: the scanner falls back to reading a photo of the barcode. Styles: pk-vin-scan.css. */
(function () {
  'use strict';
  if (window.PkVinScan) return;

  var PONYFILL_URL = 'https://cdn.jsdelivr.net/npm/barcode-detector@3.2.2/dist/iife/ponyfill.js';
  var PONYFILL_SRI = 'sha384-KJVUIUGb4pmfpWXfD8j3y9ARn6KoGC2vPC/wNUX1O1ZkYemh0BsuvsmdEU2+wS7O';
  var FORMATS = ['code_39', 'code_128', 'data_matrix', 'qr_code'];
  var SCAN_INTERVAL = 120;
  var PHOTO_MAX_EDGE = 3000;
  // Brent 2026-10-13: phones and tablets only. Computers driven by a mouse / trackpad (hover + fine pointer) get no
  // camera button; pk-vin-scan.css hides it with the same media query.
  var DESKTOP_QUERY = '(hover: hover) and (pointer: fine)';

  function available() {
    return !(window.matchMedia && window.matchMedia(DESKTOP_QUERY).matches);
  }

  var ICON_CAMERA = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 8h3l1.6-2.4a1 1 0 0 1 .8-.6h5.2a1 1 0 0 1 .8.6L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z"/><circle cx="12" cy="13.5" r="3.5"/></svg>';
  var ICON_CLOSE = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';

  /* ---------------- VIN parsing ---------------- */
  var TRANSLIT = { A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8, J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9, S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9 };
  var WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];

  function checkDigitOk(vin) {
    var sum = 0;
    for (var i = 0; i < 17; i++) {
      var c = vin.charAt(i);
      var n = /[0-9]/.test(c) ? Number(c) : TRANSLIT[c];
      if (n === undefined) return false;
      sum += n * WEIGHTS[i];
    }
    var rem = sum % 11;
    return vin.charAt(8) === (rem === 10 ? 'X' : String(rem));
  }

  // Door-jamb barcodes carry the 17-character VIN, sometimes with an "I" (import) prefix or extra data around it.
  // Take the first 17-character window that is a well-formed VIN with a correct check digit.
  function extractVin(raw) {
    var s = String(raw || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    for (var i = 0; i + 17 <= s.length; i++) {
      var w = s.slice(i, i + 17);
      if (/^[A-HJ-NPR-Z0-9]{17}$/.test(w) && checkDigitOk(w)) return w;
    }
    return '';
  }

  /* ---------------- decoder ---------------- */
  var detectorPromise = null;

  function loadScript(src, integrity) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      if (integrity) { s.integrity = integrity; s.crossOrigin = 'anonymous'; }
      s.onload = function () { resolve(); };
      s.onerror = function () { reject(new Error('Scanner library failed to load')); };
      document.head.appendChild(s);
    });
  }

  function nativeDetector() {
    var BD = window.BarcodeDetector;
    if (!BD || typeof BD.getSupportedFormats !== 'function') return Promise.resolve(null);
    return BD.getSupportedFormats().then(function (supported) {
      var want = FORMATS.filter(function (f) { return supported.indexOf(f) >= 0; });
      return want.indexOf('code_39') >= 0 ? new BD({ formats: want }) : null;
    }).catch(function () { return null; });
  }

  function ponyfillDetector() {
    var ready = window.BarcodeDetectionAPI ? Promise.resolve() : loadScript(PONYFILL_URL, PONYFILL_SRI);
    return ready.then(function () {
      var api = window.BarcodeDetectionAPI;
      if (!api || !api.BarcodeDetector) throw new Error('Scanner library missing');
      // start the WebAssembly download now rather than on the first frame
      if (typeof api.prepareZXingModule === 'function') {
        try { api.prepareZXingModule({ fireImmediately: true }); } catch (e) { /* loads on first detect instead */ }
      }
      return new api.BarcodeDetector({ formats: FORMATS });
    });
  }

  function getDetector() {
    if (!detectorPromise) {
      detectorPromise = nativeDetector().then(function (d) { return d || ponyfillDetector(); });
      detectorPromise.catch(function () { detectorPromise = null; });
    }
    return detectorPromise;
  }

  /* ---------------- dialog ---------------- */
  var ui = null;
  var state = { open: false, input: null, trigger: null, stream: null, detector: null, timer: 0, busy: false, torch: false, lastMiss: 0, firstFrame: false, resumeLive: false };

  function noop() {}

  function build() {
    if (ui) return ui;
    var root = document.createElement('div');
    root.className = 'pk-scan';
    root.hidden = true;
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-labelledby', 'pk-scan-title');
    root.innerHTML =
      '<div class="pk-scan__panel">' +
        '<div class="pk-scan__head">' +
          '<h2 class="pk-scan__title" id="pk-scan-title">Scan Your VIN</h2>' +
          '<button type="button" class="pk-scan__close" aria-label="Close the scanner">' + ICON_CLOSE + '</button>' +
        '</div>' +
        '<p class="pk-scan__lead">Aim at the barcode printed next to the VIN on the driver\'s door jamb sticker. Turning your phone sideways helps the barcode fill the box.</p>' +
        '<div class="pk-scan__stage">' +
          '<video class="pk-scan__video" playsinline muted autoplay></video>' +
          '<div class="pk-scan__frame" aria-hidden="true"><span class="pk-scan__sweep"></span></div>' +
        '</div>' +
        '<p class="pk-scan__status" role="status" aria-live="polite"></p>' +
        '<div class="pk-scan__actions">' +
          '<button type="button" class="pk-scan__btn pk-scan__light" hidden>Turn Light On</button>' +
          '<button type="button" class="pk-scan__btn pk-scan__photo">Use A Photo Instead</button>' +
          '<button type="button" class="pk-scan__btn pk-scan__cancel">Cancel</button>' +
          '<input type="file" class="pk-scan__file" accept="image/*" capture="environment" hidden>' +
        '</div>' +
      '</div>';
    document.body.appendChild(root);

    ui = {
      root: root,
      panel: root.querySelector('.pk-scan__panel'),
      stage: root.querySelector('.pk-scan__stage'),
      video: root.querySelector('.pk-scan__video'),
      status: root.querySelector('.pk-scan__status'),
      light: root.querySelector('.pk-scan__light'),
      photo: root.querySelector('.pk-scan__photo'),
      cancel: root.querySelector('.pk-scan__cancel'),
      close: root.querySelector('.pk-scan__close'),
      file: root.querySelector('.pk-scan__file')
    };
    ui.video.muted = true;
    ui.video.setAttribute('playsinline', '');

    ui.close.addEventListener('click', function () { close(); });
    ui.cancel.addEventListener('click', function () { close(); });
    root.addEventListener('click', function (evt) { if (evt.target === root) close(); });
    ui.light.addEventListener('click', toggleLight);
    ui.photo.addEventListener('click', usePhoto);
    ui.file.addEventListener('change', readPhoto);
    root.addEventListener('keydown', onKeydown);
    // Taking a photo opens the phone's camera app and hides this page: pause the live camera, don't close
    document.addEventListener('visibilitychange', function () {
      if (!state.open) return;
      if (document.hidden) {
        if (state.stream) { state.resumeLive = true; pauseLive(); }
      } else if (state.resumeLive) {
        state.resumeLive = false;
        startLive();
      }
    });
    return ui;
  }

  function setStatus(text, tone) {
    if (!ui) return;
    ui.status.textContent = text || '';
    ui.status.className = 'pk-scan__status' + (tone ? ' pk-scan__status--' + tone : '');
  }

  function onKeydown(evt) {
    if (evt.key === 'Escape' || evt.key === 'Esc') { evt.preventDefault(); close(); return; }
    if (evt.key !== 'Tab') return;
    // keep keyboard focus inside the dialog
    var items = Array.prototype.filter.call(ui.panel.querySelectorAll('button'), function (b) { return !b.hidden && !b.disabled; });
    if (!items.length) return;
    var first = items[0], last = items[items.length - 1];
    if (evt.shiftKey && document.activeElement === first) { evt.preventDefault(); last.focus(); }
    else if (!evt.shiftKey && document.activeElement === last) { evt.preventDefault(); first.focus(); }
  }

  function stopStream(stream) {
    if (!stream) return;
    stream.getTracks().forEach(function (t) { try { t.stop(); } catch (e) { /* already stopped */ } });
  }

  function cameraTrack() { return state.stream && state.stream.getVideoTracks()[0]; }

  function tuneTrack(track) {
    if (!track || typeof track.getCapabilities !== 'function') return;
    var caps = {};
    try { caps = track.getCapabilities() || {}; } catch (e) { return; }
    if (caps.focusMode && caps.focusMode.indexOf('continuous') >= 0) {
      track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] }).catch(noop);
    }
    if (caps.torch) { ui.light.hidden = false; ui.light.textContent = 'Turn Light On'; }
  }

  function toggleLight() {
    var track = cameraTrack();
    if (!track) return;
    var next = !state.torch;
    track.applyConstraints({ advanced: [{ torch: next }] }).then(function () {
      state.torch = next;
      ui.light.textContent = next ? 'Turn Light Off' : 'Turn Light On';
    }).catch(function () { ui.light.hidden = true; });
  }

  function namedError(name) { var e = new Error(name); e.name = name; return e; }

  function startCamera() {
    var md = navigator.mediaDevices;
    if (!md || typeof md.getUserMedia !== 'function') return Promise.reject(namedError('NoCameraApi'));
    return md.getUserMedia({
      audio: false,
      video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } }
    }).then(function (stream) {
      if (!state.open || document.hidden) {
        stopStream(stream);
        if (state.open) state.resumeLive = true;
        throw namedError('Closed');
      }
      state.stream = stream;
      ui.video.srcObject = stream;
      tuneTrack(cameraTrack());
      var played = ui.video.play();
      return played && typeof played.catch === 'function' ? played.catch(noop) : null;
    });
  }

  function cameraFailed(err) {
    var name = (err && err.name) || '';
    if (!state.open || name === 'Closed') return;
    ui.stage.hidden = true;
    var msg;
    if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') {
      msg = 'Camera access is blocked. Allow the camera for this site in your browser settings, or use a photo of the barcode instead.';
    } else if (name === 'NotFoundError' || name === 'OverconstrainedError' || name === 'DevicesNotFoundError') {
      msg = 'No camera was found. Use a photo of the barcode instead.';
    } else if (name === 'NoCameraApi') {
      msg = 'This browser can\'t open the camera here. Use a photo of the barcode instead.';
    } else {
      msg = 'The camera couldn\'t start. Use a photo of the barcode instead, or type your VIN in.';
    }
    setStatus(msg, 'warn');
    ui.photo.focus();
  }

  function decoderFailed() {
    if (!state.open) return;
    stopScanning();
    ui.stage.hidden = true;
    ui.photo.hidden = true;
    ui.light.hidden = true;
    setStatus('The scanner couldn\'t load right now. Please type your VIN in instead.', 'error');
    ui.cancel.focus();
  }

  // 'vin' when a VIN was found (and applied), 'other' when barcodes were read but none is a VIN, 'none' otherwise
  function handleCodes(codes) {
    if (!state.open) return 'none';
    var list = codes || [];
    for (var i = 0; i < list.length; i++) {
      var vin = extractVin(list[i] && list[i].rawValue);
      if (vin) { finish(vin); return 'vin'; }
    }
    return list.length ? 'other' : 'none';
  }

  function scanFrame() {
    state.timer = 0;
    if (!state.open || !state.detector) return;
    var v = ui.video;
    if (!state.busy && v.readyState >= 2 && v.videoWidth) {
      state.busy = true;
      state.detector.detect(v).then(function (codes) {
        if (!state.firstFrame && state.open) { state.firstFrame = true; setStatus('Looking for the VIN barcode…'); }
        var result = handleCodes(codes);
        if (result === 'other' && Date.now() - state.lastMiss > 2500) {
          state.lastMiss = Date.now();
          setStatus('That barcode isn\'t a VIN. Aim at the one printed next to the VIN.', 'warn');
        }
      }).catch(noop).then(function () { state.busy = false; });
    }
    if (state.open) state.timer = window.setTimeout(scanFrame, SCAN_INTERVAL);
  }

  function stopScanning() {
    if (state.timer) window.clearTimeout(state.timer);
    state.timer = 0;
    state.detector = null;
    state.busy = false;
  }

  function imageCanvas(file) {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        var w = img.naturalWidth, h = img.naturalHeight;
        var k = Math.min(1, PHOTO_MAX_EDGE / Math.max(w, h));
        var c = document.createElement('canvas');
        c.width = Math.max(1, Math.round(w * k));
        c.height = Math.max(1, Math.round(h * k));
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        URL.revokeObjectURL(url);
        resolve(c);
      };
      img.onerror = function () { URL.revokeObjectURL(url); reject(new Error('Unreadable image')); };
      img.src = url;
    });
  }

  function readPhoto() {
    var file = ui.file.files && ui.file.files[0];
    if (!file) return;
    setStatus('Reading the photo…');
    getDetector().then(function (det) {
      return imageCanvas(file).then(function (canvas) { return det.detect(canvas); });
    }).then(function (codes) {
      var result = handleCodes(codes);
      if (result === 'other') setStatus('The barcode in that photo isn\'t a VIN. Photograph the one printed next to the VIN.', 'warn');
      else if (result === 'none') setStatus('We couldn\'t find a barcode in that photo. Get closer so the barcode fills the shot and is sharp, or type your VIN in.', 'warn');
    }).catch(function () {
      if (state.open) setStatus('We couldn\'t read that photo. Try again, or type your VIN in.', 'warn');
    });
  }

  function startLive() {
    state.torch = false;
    state.firstFrame = false;
    ui.stage.hidden = false;
    ui.light.hidden = true;
    setStatus('Starting the camera…');
    var detector = getDetector();
    detector.catch(noop);
    startCamera().then(function () {
      if (!state.open || !state.stream) return;
      setStatus('Loading the scanner…');
      return detector.then(function (d) {
        if (!state.open || !state.stream) return;
        state.detector = d;
        scanFrame();
      }, decoderFailed);
    }, cameraFailed);
  }

  function pauseLive() {
    stopScanning();
    stopStream(state.stream);
    state.stream = null;
    state.torch = false;
    if (ui) ui.video.srcObject = null;
  }

  function usePhoto() {
    state.resumeLive = false;
    pauseLive();
    ui.stage.hidden = true;
    ui.light.hidden = true;
    ui.photo.textContent = 'Take A Photo';
    setStatus('Take a close, sharp photo of the barcode next to the VIN.');
    ui.file.value = '';
    ui.file.click();
  }

  function open(input, trigger) {
    build();
    if (state.open) close();
    state.open = true;
    state.input = input;
    state.trigger = trigger || null;
    state.lastMiss = 0;
    state.resumeLive = false;
    ui.photo.hidden = false;
    ui.photo.textContent = 'Use A Photo Instead';
    ui.root.hidden = false;
    document.documentElement.classList.add('pk-scan-lock');
    ui.close.focus();
    startLive();
  }

  function close() {
    if (!ui || !state.open) return;
    state.open = false;
    state.resumeLive = false;
    pauseLive();
    ui.root.hidden = true;
    document.documentElement.classList.remove('pk-scan-lock');
    var trigger = state.trigger;
    state.trigger = null;
    if (trigger && document.contains(trigger)) {
      try { trigger.focus({ preventScroll: true }); } catch (e) { trigger.focus(); }
    }
  }

  function finish(vin) {
    var input = state.input;
    state.input = null;
    close();
    if (navigator.vibrate) { try { navigator.vibrate(60); } catch (e) { /* no haptics */ } }
    if (!input || !document.contains(input)) return;
    input.value = vin;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ---------------- camera button ---------------- */
  function attach(input) {
    if (!input || !input.parentNode || input.getAttribute('data-pk-vin-scan') === '1') return;
    input.setAttribute('data-pk-vin-scan', '1');
    var wrap = document.createElement('div');
    wrap.className = 'pk-vin-scan';
    input.parentNode.insertBefore(wrap, input);
    wrap.appendChild(input);
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'pk-vin-scan__btn';
    btn.title = 'Scan The VIN Barcode';
    btn.setAttribute('aria-label', 'Scan the VIN barcode with your camera');
    btn.innerHTML = ICON_CAMERA;
    btn.addEventListener('click', function (evt) {
      evt.preventDefault();
      evt.stopPropagation();
      open(input, btn);
    });
    wrap.appendChild(btn);
  }

  window.PkVinScan = { attach: attach, open: open, close: close, extractVin: extractVin, available: available };
})();
